import { type IDateValidator, parseDateValidators } from "forms/helpers/parseDateValidators";
import type { IProperty } from "types/jsonSchema";
import { z } from "zod";

const formatDateToString = (date: Date): string => {
    return date.toISOString().split("T")[0];
};

export default function parseDate(field: IProperty) {
    const baseSchema = z.coerce.date();

    const validators = field?.["x-validators"] as IDateValidator | undefined;
    if (!validators) {
        return baseSchema.transform(formatDateToString);
    }

    const { minDate, maxDate } = parseDateValidators(validators);

    const isExactDate = minDate instanceof Date && maxDate instanceof Date && minDate.getTime() === maxDate.getTime();
    if (isExactDate) {
        const exactDate = minDate;
        return baseSchema
            .superRefine((val, ctx) => {
                const exactDateOnly = new Date(exactDate.getFullYear(), exactDate.getMonth(), exactDate.getDate());
                const valDateOnly = new Date(val.getFullYear(), val.getMonth(), val.getDate());

                if (valDateOnly.getTime() !== exactDateOnly.getTime()) {
                    const isTooSmall = valDateOnly.getTime() < exactDateOnly.getTime();
                    const baseIssue = { type: "date" as const, inclusive: true, exact: true };

                    ctx.issues.push(
                        isTooSmall
                            ? { code: "too_small", minimum: exactDate.getTime(), origin: "date", input: val, ...baseIssue }
                            : { code: "too_big", maximum: exactDate.getTime(), origin: "date", input: val, ...baseIssue },
                    );
                }
            })
            .transform(formatDateToString);
    }

    let schema = baseSchema;
    if (minDate) schema = schema.min(minDate);
    if (maxDate) schema = schema.max(maxDate);

    return schema.transform(formatDateToString);
}
